import { connect } from 'react-redux';
import { useState, useEffect, useRef, } from 'react';

import { ERROR_MESSAGES } from '../errorMessages';
import { login, setLoading, setAlert, siteboardAction } from '../actions/defaultActions';
import {
  Email, SiteboardApiValidator, UserName, GET_SUBSCRIPTION_INVITE_LINK_DETAILS, STOP_USING_PROMO_SIGNUP_LINK, INVITE_LINK_EXPIRE_TIME_DAYS, REQUEST_FRESH_SUBSCRIPTION_INVITE_LINK,
  REDEEM_SUBSCRIPTION_INVITE_LINK, REQUEST_TRIAL_SIGNUP_CODE, REQUEST_STANDARD_SIGNUP_CODE, CHECK_TRIAL_SIGNUP_CODE, REQUEST_PROMO_SIGNUP_CODE, SessionKey, CHECK_PROMO_SIGNUP_CODE,
} from '../actions/types';
import { isObject, getServerApiResponse, } from '../Util';

import PoweredByBar from './PoweredByBar';
import ParallaxSection from './ParallaxSection';
import Tooltip from './Tooltip';
import PushButton from './PushButton';

import { Gift, Sparkles, Mail, } from 'lucide-react';
import Logo from '../SiteBoard_Logo';
import BackIcon from '../icons/backIcon';

const PAGE_PROMO_SIGNUP = 'PAGE_PROMO_SIGNUP';
const PAGE_PROMO_LOGIN_CODE = 'PAGE_PROMO_LOGIN_CODE';
const PAGE_PROMO_LOGIN_LINK = 'PAGE_PROMO_LOGIN_LINK';

const inputStyle = 'w-full text-white rounded-md px-3 py-1.5 bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]';
//const disabledInputStyle = 'w-full text-white rounded-md px-3 py-1.5 bg-[#444247] focus:bg-[#656565]';

const PromoSignupPage = ({_promoSignupLinkCode, _isTouchDevice, setAlert, login, setLoading, siteboardAction}) => {

  const [currentPage, setCurrentPage] = useState(PAGE_PROMO_SIGNUP);
  const [inputFirstName, setInputFirstName] = useState('');
  const [inputLastName, setInputLastName] = useState('');
  const [inputEmail, setInputEmail] = useState('');
  const [inputLoginCode, setInputLoginCode] = useState('');
 
  const loginCodeRef = useRef(null);

  // focus code input when component is shown
  useEffect(() => { 
    // Don't auto focus for touch devices because it changes the screen layout too much when the virtual keyboard appears
    if(_isTouchDevice === true || !loginCodeRef.current || currentPage !== PAGE_PROMO_LOGIN_CODE) return;
    loginCodeRef.current.select();
  }, [currentPage, _isTouchDevice]);

  const requestSignupCode = () => {
    let validInput = null;
    try {
      validInput = SiteboardApiValidator[REQUEST_PROMO_SIGNUP_CODE].validateInput({ 
        action: REQUEST_PROMO_SIGNUP_CODE,
        email: inputEmail,
        firstName: inputFirstName,
        lastName: inputLastName,
        promoCode: _promoSignupLinkCode,
      });
      if (validInput.firstName.length === 0) throw new Error('Please enter your first name');
      if (validInput.lastName.length === 0) throw new Error('Please enter your last name');
    } 
    catch(error) { 
      setAlert(error.message); 
      return;
    }

    const onApiSuccess = (data) => {
      setLoading('');
      if(data.action === 'loginCodeEmailed') {
        setCurrentPage(PAGE_PROMO_LOGIN_CODE);
      } else if(data.action === 'loginLinkEmailed') {
        setCurrentPage(PAGE_PROMO_LOGIN_LINK);
      } else {
        setAlert(ERROR_MESSAGES.LOGIN);
        return;
      }
    };
    const onApiError = (errorMessage) => {
      setLoading('');
      setAlert(typeof errorMessage === 'string' ? errorMessage : ERROR_MESSAGES.LOGIN);
    };

    setLoading('Emailing Your Verification Code');
    getServerApiResponse(validInput, onApiSuccess, onApiError);
  };

  const submitSignupCode = () => {
    let validInput = null;
    try {
      validInput = SiteboardApiValidator[CHECK_PROMO_SIGNUP_CODE].validateInput({
        action: CHECK_PROMO_SIGNUP_CODE,
        email: inputEmail,
        loginCode: inputLoginCode,
        firstName: inputFirstName,
        lastName: inputLastName,
        promoCode: _promoSignupLinkCode,
      });
    } 
    catch(error) { 
      setAlert(error.message); 
      return;
    }

    const onApiSuccess = (data) => {
      setLoading('');
      try {
        if (!isObject(data.user) || !SessionKey.isValid(data.sessionKey)) throw new Error();
        login(data.user, data.sessionKey); 
        window.history.pushState('', '', '?'); // clear the url
        siteboardAction(STOP_USING_PROMO_SIGNUP_LINK, {});
      } catch(error) {
        setAlert(ERROR_MESSAGES.LOGIN_CODE);
      }
    };
    const onApiError = (errorMessage) => {
      setLoading('');
      setAlert(typeof errorMessage === 'string' ? errorMessage : ERROR_MESSAGES.LOGIN_CODE);
    };
    setLoading('Checking Verification Code');
    getServerApiResponse(validInput, onApiSuccess, onApiError);
  };

  return (
    <div className={`relative flex flex-col justify-between min-h-screen bg-[#151515] text-white overflow-hidden`}>
      <div className='relative z-[1] flex justify-between p-4'>
        {/* SiteBoard logo */}
        <a 
          className={'flex items-center gap-x-2 font-bold text-[1.8em]'}
          href={process.env.LIVE_MODE === true ? process.env.REACT_APP_WEBSITE_DOMAIN : 'http://localhost:3000/'}
        >
          <Logo size={'1em'} />
          {process.env.REACT_APP_WEBSITE_NAME} 
        </a>
        <div className='flex justify-end'></div>
      </div>
      {currentPage === PAGE_PROMO_SIGNUP &&
        <div className='relative z-[1] grow flex flex-col justify-center items-center max-w-screen-lg px-2 text-[1.2em] w-full animate-right-fade mx-auto'>
          <div className='grow flex flex-col justify-center items-center max-w-[500px] mt-[20px] w-full'>
            <div className='flex flex-col gap-y-8 w-full'>
              <div className="flex flex-col items-start gap-4 w-full">

                {/* Animated gift icon */}
                <div className="relative group">

                  {/* Pulsing glow */}
                  <div className="
                    absolute inset-0 rounded-2xl
                    bg-[#f9a928]/25 blur-xl
                    animate-pulse
                  " />

                  {/* Icon container */}
                  <div className="
                    relative flex items-center justify-center
                    w-14 h-14 rounded-2xl
                    bg-[#f9a928]/10
                    border border-[#f9a928]/40
                    shadow-[0_0_25px_rgba(249,169,40,0.12)]
                    animate-[promoFloat_3s_ease-in-out_infinite]
                    transition-all duration-300
                    group-hover:scale-110
                    group-hover:rotate-[-8deg]
                  ">
                    <Gift
                      size={28}
                      strokeWidth={1.8}
                      className="text-[#f9a928]"
                    />
                  </div>

                  {/* Sparkles */}
                  <Sparkles
                    size={17}
                    className="
                      absolute -top-2 -right-3
                      text-[#f9a928]
                      animate-[promoTwinkle_2s_ease-in-out_infinite]
                    "
                  />

                  <Sparkles
                    size={12}
                    className="
                      absolute -bottom-2 -left-3
                      text-[#a58aff]
                      animate-[promoTwinkle_2s_ease-in-out_infinite_0.8s]
                    "
                  />
                </div>

                {/* Heading and description */}
                <div className="flex flex-col gap-y-2 w-full">
                  <div className="
                    font-bold
                    text-[1.8em] sm:text-[2.2em] 2xl:text-[2.6em]
                    text-white text-left leading-[1.1em]
                  ">
                    You've been invited!
                  </div>

                  <div className="text-white/70 text-left leading-relaxed">
                    You've unlocked{' '}
                    <span className="text-[#f9a928] font-semibold">
                      complimentary access
                    </span>{' '}
                    to {process.env.REACT_APP_WEBSITE_NAME} through a special
                    invitation. Create your account below to get started.
                  </div>
                </div>

                <style>{`
                  @keyframes promoFloat {
                    0%, 100% {
                      transform: translateY(0);
                    }
                    50% {
                      transform: translateY(-5px);
                    }
                  }

                  @keyframes promoTwinkle {
                    0%, 100% {
                      opacity: 0.3;
                      transform: scale(0.8) rotate(0deg);
                    }
                    50% {
                      opacity: 1;
                      transform: scale(1.2) rotate(15deg);
                    }
                  }

                  @media (prefers-reduced-motion: reduce) {
                    .animate-\\[promoFloat_3s_ease-in-out_infinite\\],
                    .animate-\\[promoTwinkle_2s_ease-in-out_infinite\\],
                    .animate-\\[promoTwinkle_2s_ease-in-out_infinite_0\\.8s\\] {
                      animation: none;
                    }
                  }
                `}</style>
              </div>
              <div className='flex flex-col gap-y-6'>
                {/* First and Last name */}
                <div className='flex gap-x-2'>
                  <div>
                    <label className='block text-sm font-medium mb-1'>First Name</label>
                    <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                      <input 
                        value={inputFirstName}
                        className={inputStyle}
                        type='text' 
                        onChange={(event) => { setInputFirstName(event.target.value); }}
                        onKeyDown={(event) => { if(event.key === 'Enter') { requestSignupCode(); } }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className='block text-sm font-medium mb-1'>Last Name</label>
                    <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                      <input 
                        value={inputLastName}
                        className={inputStyle}
                        type='text' 
                        onChange={(event) => { setInputLastName(event.target.value); }}
                        onKeyDown={(event) => { if(event.key === 'Enter') { requestSignupCode(); } }}
                      />
                    </div>
                  </div>
                </div>
                {/* Email Input */}
                <div>
                  <label className='block text-sm font-medium mb-1'>Work Email</label>
                  <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                    <input 
                      value={inputEmail}
                      className={inputStyle}
                      type='text' 
                      onChange={(event) => { setInputEmail(event.target.value); }}
                      onKeyDown={(event) => { if(event.key === 'Enter') { requestSignupCode(); } }}
                    />
                  </div>
                </div>
                {/* submit email button */}
                <PushButton 
                  textColor='black'
                  bgColor='#f9a928'
                  hoverBgColor='#f79800'
                  onClick={() => { requestSignupCode(); }}
                >
                  Continue
                </PushButton>
              </div>
            </div>
          </div>
        </div>
      }
      {currentPage === PAGE_PROMO_LOGIN_CODE &&
        <div className='relative z-[1] grow flex flex-col justify-center items-center max-w-screen-lg w-full mx-auto text-[1.2em] px-2 pb-0 animate-right-fade'>
          <div className='grow flex flex-col justify-center gap-y-4 max-w-[350px]'>

            {/* Email Icon */}
            <div>
              <div className="inline-block relative">
                <div className="absolute inset-0 rounded-2xl bg-[#471cff]/20 blur-xl" />

                <div className="
                  relative flex h-16 w-16 items-center justify-center
                  rounded-2xl border border-[#7657ff]/30
                  bg-gradient-to-br from-[#471cff]/25 to-[#471cff]/5
                  shadow-lg shadow-[#471cff]/10
                ">
                  <Mail
                    size={30}
                    strokeWidth={1.6}
                    className="text-[#a997ff]"
                  />
                </div>
              </div>
            </div>

            <div className='text-[1.8em] text-white font-bold text-left'>Check Your Email</div>
            {/* login code input */}
            <div className='max-w-full flex flex-col gap-y-4 mx-auto'>
              <div className='flex flex-col gap-3 mt-[4px]'>
                <div>
                  <div>We've sent a verification code to</div>
                  <div className='font-bold'>{inputEmail}</div>
                </div>
              </div>
              <div className='font-light opacity-60 mx-auto'>Didn&apos;t receive the code? Check your spam folder or wait up to 2 minutes for delivery.</div>
              <div>
                <label className='block text-sm font-medium mb-1'>Verification Code</label>
                <input 
                  ref={loginCodeRef}
                  value={inputLoginCode}
                  className={inputStyle + ' text-center'}
                  type='text' 
                  placeholder="Enter your code"
                  onChange={(event) => { setInputLoginCode(event.target.value); }}
                  onKeyDown={(event) => { if(event.key === 'Enter') { submitSignupCode(); } }}
                />
              </div>
              {/* submit login code button */}
              <PushButton 
                textColor='white'
                bgColor='#471cff'
                hoverBgColor='#603aff'
                onClick={() => { submitSignupCode(); }}
              >
                Continue
              </PushButton>

              {/* Resend Code */}
              <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-sm text-gray-500">
                  Didn't receive the code?
                </p>

                <button
                  type="button"
                  className="
                    group inline-flex items-center gap-2
                    text-sm font-semibold text-[#b5a7ff]
                    transition-colors duration-200
                    hover:text-white
                    focus-visible:outline focus-visible:outline-2
                    focus-visible:outline-offset-4
                    focus-visible:outline-[#b5a7ff]
                  "
                  onClick={() => {
                    requestSignupCode();
                    setInputLoginCode("");
                  }}
                >
                  <svg
                    className="
                      h-4 w-4
                      transition-transform duration-500
                      group-hover:rotate-180
                    "
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 11a8 8 0 1 0-2.5 6" />
                    <path d="M20 4v7h-7" />
                  </svg>

                  Resend verification code
                </button>
              </div>
            </div>
          </div>

          {/* Back Button */}
          <div className='pb-5 w-full'>
            <button 
              className='self-start group flex justify-center items-center gap-x-2' 
              onClick={() => { 
                try { setCurrentPage(PAGE_PROMO_SIGNUP); } catch(error) {}
              }}
            >
              <div className='group-hover:hidden'><BackIcon color='#a59e9e' /></div>
              <div className='hidden group-hover:block'><BackIcon color='#fff' /></div>
              <div className='text-[#a59e9e] group-hover:text-white'>Back</div>
            </button>
          </div>
        </div>
      }
      {currentPage === PAGE_PROMO_LOGIN_LINK &&
        <div className='relative z-[1] grow flex flex-col justify-center items-center max-w-screen-lg w-full mx-auto text-[1.2em] px-2 pb-0 animate-right-fade'>
          <div className='grow flex flex-col justify-center gap-y-6 max-w-[350px]'>
            <div className='text-[1.8em] text-white font-bold text-left'>Check Your Email</div>
            <div className='w-[300px] max-w-full flex flex-col gap-y-4 mx-auto'>
              <div>We've sent an email to <b>{inputEmail}</b> containing a secure link to complete the account creation process. Please check your inbox and follow the provided link to finalize your account setup.</div>
              <div className='font-light opacity-50 mx-auto'>Didn&apos;t receive the email? Check your spam folder or wait up to 2 minutes for delivery.</div>
            </div>
          </div>
          {/* Back Button */}
          <div className='pb-5 w-full'>
            <button 
              className='self-start group flex justify-center items-center gap-x-2' 
              onClick={() => { 
                try { setCurrentPage(PAGE_PROMO_SIGNUP); } catch(error) {}
              }}
            >
              <div className='group-hover:hidden'><BackIcon color='#a59e9e' /></div>
              <div className='hidden group-hover:block'><BackIcon color='#fff' /></div>
              <div className='text-[#a59e9e] group-hover:text-white'>Back</div>
            </button>
          </div>
        </div>
      }
      <div className='z-[1]'>
        <PoweredByBar />
      </div>
      {/* construction site background */}
      <div className='z-[0] absolute top-[0vh] left-[0vw] opacity-[1] pointer-events-none'>
        <ParallaxSection parallaxMultiplier={0.5}>
          {/*<img width='906' height='1000' className='h-[120vh] object-cover object-top' src='/images/constructionSite2.svg' alt='line drawing of a construction site' />*/}
          <img width='1536' height='1024' className='w-[100vw] min-h-[100vh] object-cover object-top' src='/images/constructionSite3.jpg' alt='a construction site' />
          {/* gradient at bottom to fade image into gray background */}
          <div className='absolute bottom-0 left-0 h-[15vh] w-[100vw] bg-gradient-to-t from-[#151515] to-[#15151500]'></div>
        </ParallaxSection>
      </div>
      {/* top right floating logo */}
      <div className='fixed top-[4em] left-[100%] translate-x-[-90%] opacity-[0.10] pointer-events-none'>
        <div className='sm:hidden'><Logo size={250} /></div>
        <div className='hidden sm:block 2xl:hidden'><Logo size={300} /></div>
        <div className='hidden 2xl:block'><Logo size={400} /></div>
      </div>
    </div>
  );
}

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _promoSignupLinkCode: state.localEphemeral.promoSignupLinkCode,
  _isTouchDevice: state.localEphemeral.isTouchDevice,
}};
export default connect(mapStateToProps, {setAlert, login, setLoading, siteboardAction})(PromoSignupPage);
